import { pickDefaultModelResult } from '@upright/common/model/data-point-pickers';
import { ModelEntityType } from '@upright/common/model/input/types';
import { mergeImpactScores } from '@upright/common/model/output/scores';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { useEntitySearchQueries } from '@upright/ui/api/model/search/hooks';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { NetImpactChart } from '@upright/ui/common/upright/model/output/scores/net-impact-chart';
import { ImpactChartDataSeries } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/types';
import { useFetchMixinComparisonDataQuery } from '@upright/ui/views/internal/mixins-view/mixin-preview/use-model-entity-data-query';
import { scaleLinear } from 'd3-scale';
import { keyBy, max, min } from 'lodash';
import { useState } from 'react';
import styled from 'styled-components';
import { InternalBreadcrumbs } from '../common/internal-breadcrumps';

export function MagicView() {
  const [text, setText] = useState('');
  const { semanticSearchQuery } = useEntitySearchQueries(text, [ModelEntityType.PRODUCT], 'only semantic', {
    enabled: text.length > 0,
  });
  const { data } = semanticSearchQuery;
  const { data: scoreData } = useFetchMixinComparisonDataQuery(data || []);
  const isTeam = useIsTeam();

  if (!isTeam) {
    return <div>Must be logged in Upright team user to use this page.</div>;
  }

  function getDataSeries() {
    if (!scoreData || !data) {
      return;
    }
    const minRankScore = min(data.map(d => d.score)) || 0;
    const maxRankScore = max(data.map(d => d.score)) || 0;

    const weightScale = scaleLinear().domain([minRankScore, maxRankScore]).range([0.05, 1]);
    const searchDataById = keyBy(data, d => d.id);

    const scoreList = scoreData.map(d => {
      const result = pickDefaultModelResult(d.modelResults);
      const scores = result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE];
      const rankScore = searchDataById[d.id].score || 0;
      const weight = weightScale(rankScore);
      return { scores, weight };
    });

    const mergedScores = mergeImpactScores(
      scoreList.map(d => d.scores),
      scoreList.map(d => d.weight ?? 0),
    );
    const ret: ImpactChartDataSeries = {
      impactScoresByTIVV: {
        [FinalImpactScoreType.RELATIVE]: mergedScores,
      },
      name: text,
      id: 'foo',
      type: ModelEntityType.PRODUCT,
      color: undefined,
      resultId: undefined,
    };
    return ret;
  }

  const series = getDataSeries();

  const showChart = series && text.length > 1 && data && data.length > 0;

  const examples = [
    'iPhone',
    'Tesla',
    'Cigarettes',
    'Blockchain',
    'Serena Williams',
    'Atomic force microscopes used for research of the circulatory system',
  ];

  return (
    <Main>
      <InternalBreadcrumbs items={[{ label: 'Magic' }]} />
      <Title>What is the impact of...</Title>
      <TextArea
        rows={1}
        value={text}
        placeholder={'Enter something'}
        onChange={e => {
          const newText = e.currentTarget.value;
          setText(newText);
        }}
      />
      {text.length < 2 && (
        <>
          <EnterSomething>
            Enter <i>something</i> to view results.
          </EnterSomething>
          <Examples>
            <ExamplesTitle>Examples</ExamplesTitle>
            {examples.map(d => {
              return (
                <Example
                  key={d}
                  onClick={() => {
                    setText(d);
                  }}
                >
                  {d}
                </Example>
              );
            })}
          </Examples>
        </>
      )}
      <ChartArea>
        {series && showChart && (
          <NetImpactChart data={[series]} hideTitle={true} hideValueChainsSwitch={true} showRenderViewButton={false} />
        )}
      </ChartArea>
    </Main>
  );
}

const Main = styled.div`
  margin-top: 1.5em;
  text-align: center;
  font-size: 120%;
`;

const EnterSomething = styled.div`
  margin-top: 1em;
`;

const Title = styled.div`
  margin-bottom: 0.8em;
  font-weight: 500;
`;
const ExamplesTitle = styled.div`
  margin-bottom: 0.8em;
  margin-top: 0.8em;
  font-weight: 500;
  cursor: pointer;
`;

const Example = styled.div`
  margin-bottom: 0.8em;
  cursor: pointer;
`;

const Examples = styled.div`
  margin-top: 1.2em;
  padding-top: 0.8em;
  padding-bottom: 0.8em;
  margin-left: auto;
  margin-right: auto;
  background-color: ${BouColor.TOOL_TABLE_BACKGROUND_LIGHT_GRAY};
  max-width: 40em;
`;

const ChartArea = styled.div`
  margin-top: 2em;
  margin-right: auto;
  margin-left: auto;
  max-width: 1000px;
`;

const TextArea = styled.textarea`
  margin-left: auto;
  margin-right: auto;
  max-width: 40em;
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 0.4em;
  width: 100%;
`;
